import React, {useCallback, useMemo, useState} from 'react';
import {useIntl} from 'umi';
import {Bubble, Input, List, ListItem, Select} from 'tea-component';
import NumberInput from '@/pages/datalab/components/NumberInput';
import DateTypeSegment from "@/pages/datalab/components/DateComponents/DateTypeSegment";
import {getLastDateRange} from "@/pages/datalab/utils/utils";
import './index.scss';
import {LANGUAGE_EN} from '@home/_config/config';
import TimeCompare from "@/pages/datalab/components/DateComponents/TimeCompare";
import {adjustOverlayPositionByPlacement} from "@/pages/datalab/utils/overlayPositionAdjuster";
import { DateGranularity, DateShowType, TDateGranularity } from '@dbl/core/date';
import classNames from 'classnames';

interface ListOption {
  text: string;
  value: string | number;
}

// export const ThisYear = 'thisYear';

export interface DateValue {
  dateRange: [string, string];
  dateType: TDateGranularity;
  compareTimes: [];
  quickValue: {
    tab: string;
    commonType?: string;
    eventType?: string;
    eventId?: string;
  }; // 快速选择
}

export interface DynamicDateValue extends DateValue{
  text?: string; // 展示的文本
  lastCount: number | string; // 最近几天
  value: string[];
  from: string; //
}



/**
 * 静态时间范围选择
 * @param props
 * @constructor
 */
function DynamicDate(props: any) {
  const { defaultValue={}, onChange=()=>{}, className='', style, onClose, isDisabledCompare, getOverlayPositionConfig, fieldName } = props;
  const { formatMessage, locale } = useIntl();
  const isEn = locale === LANGUAGE_EN;
  const [value, setValue] = useState<any>(defaultValue);
  
  
  const {optionsMap, agoOptionsMap} = useMemo(() => {
      
    const optionsMap = {
      [DateGranularity.HOUR]: [
        {
          value: -2,
          text: formatMessage({id: 'dbl_date_hour_now'})
        },
        {
          value: -1,
          text: formatMessage({id: 'dbl_date_hour_current'})
        },
        {
          value: 1,
          text: formatMessage({id: 'dbl_date_hour_last_1'})
        },
        {
          value: 6,
          text: formatMessage({id: 'dbl_date_hour_last_6'})
        },
        {
          value: 12,
          text: formatMessage({id: 'dbl_date_hour_last_12'})
        },
        {
          value: 24,
          text: formatMessage({id: 'dbl_date_hour_last_24'})
        },
        {
          value: 48,
          text: formatMessage({id: 'dbl_date_hour_last_48'})
        }
      ],
      [DateGranularity.DAY]: [
        {
          value: -1,
          text: formatMessage({id: 'dbl_date_day_last_current'})
        },
        {
          value: 1,
          text: formatMessage({id: 'dbl_date_day_last_1'})
        },
        {
          value: 3,
          text: formatMessage({id: 'dbl_date_day_last_3'})
        },
        {
          value: 7,
          text: formatMessage({id: 'dbl_date_day_last_7'})
        },
        {
          value: 14,
          text: formatMessage({id: 'dbl_date_day_last_14'})
        },
        {
          value: 30,
          text: formatMessage({id: 'dbl_date_day_last_30'})
        }
      ],
      [DateGranularity.WEEK]: [
        {
          value: -1,
          text: formatMessage({id: 'dbl_date_week_ago_current'})
        },
        {
          value: 1,
          text: formatMessage({id: 'dbl_date_week_last_1'})
        },
        {
          value: 4,
          text: formatMessage({id: 'dbl_date_week_last'}, {last: 4})
        },
        {
          value: 12,
          text: formatMessage({id: 'dbl_date_week_last'}, {last: 12})
        },
        {
          value: 24,
          text: formatMessage({id: 'dbl_date_week_last'}, {last: 24})
        },
        {
          value: 0,
          text: formatMessage({id: 'dbl_date_year'})
        },
      ],

      [DateGranularity.MONTH]: [
        {
          value: -1,
          text: formatMessage({id: 'dbl_date_month_ago_current'})
        },
        {
          value: 1,
          text: formatMessage({id: 'dbl_date_month_last_1'})
        },
        {
          value: 3,
          text: formatMessage({id: 'dbl_date_month_last'}, {last: 3})
        },
        {
          value: 6,
          text: formatMessage({id: 'dbl_date_month_last'}, {last: 6})
        },
        {
          value: 12,
          text: formatMessage({id: 'dbl_date_month_last'}, {last: 12})
        },
        {
          value: 24,
          text: formatMessage({id: 'dbl_date_month_last'}, {last: 24})
        },
        {
          value: 0,
          text: formatMessage({id: 'dbl_date_year'})
        },
      ]

    };

    const agoOptionsMap = {
      [DateGranularity.HOUR]: [
        {
          value: '-1',
          text: formatMessage({id: 'dbl_date_hour_ago_current'})
        },
        {
          value: '1',
          text: formatMessage({id: 'dbl_date_hour_ago_1'})
        },
        {
          value: '2',
          text: formatMessage({id: 'dbl_date_hour_ago_2'})
        },
        {
          value: '3',
          text: formatMessage({id: 'dbl_date_hour_ago_3'})
        },
        {
          value: '4',
          text: formatMessage({id: 'dbl_date_hour_ago'}, {ago: 4})
        },
      
      ],
      [DateGranularity.DAY]: [
        {
          value: '-1',
          text: formatMessage({id: 'dbl_date_day_ago_current'})
        },
        {
          value: '1',
          text: formatMessage({id: 'dbl_date_day_ago_1'})
        },
        {
          value: '2',
          text: formatMessage({id: 'dbl_date_day_ago_2'})
        },
        {
          value: '3',
          text: formatMessage({id: 'dbl_date_day_ago_3'})
        },
        {
          value: '4',
          text: formatMessage({id: 'dbl_date_day_ago_4'})
        }
      ],
      [DateGranularity.WEEK]: [
        {
          value: '-1',
          text: formatMessage({id: 'dbl_date_week_ago_current'})
        },
        {
          value: '1',
          text: formatMessage({id: 'dbl_date_week_ago_1'})
        },
        {
          value: '2',
          text: formatMessage({id: 'dbl_date_week_ago_2'})
        },
        {
          value: '3',
          text: formatMessage({id: 'dbl_date_week_ago_3'})
        },
        {
          value: '4',
          text: formatMessage({id: 'dbl_date_week_ago_4'})
        }
      ],

      [DateGranularity.MONTH]: [
        {
          value: '-1',
          text: formatMessage({id: 'dbl_date_month_ago_current'})
        },
        {
          value: '1',
          text: formatMessage({id: 'dbl_date_month_ago_1'})
        },
        {
          value: '2',
          text: formatMessage({id: 'dbl_date_month_ago_2'})
        },
        {
          value: '3',
          text: formatMessage({id: 'dbl_date_month_ago_3'})
        },
        {
          value: '4',
          text: formatMessage({id: 'dbl_date_month_ago_4'})
        }
      ]

    };

    return {
      optionsMap,
      agoOptionsMap
    };
  }
  , [formatMessage]);
  
  const { dateType, dateRange=[], compareTimes } = value || {};
  
  const handleUserAction = useCallback(() => {
    const {overlayEle, labelEle, ...rest} = getOverlayPositionConfig?.() || {};
    if (!overlayEle || !labelEle) return;
    
    // 增加对比日期后手动调整位置
    adjustOverlayPositionByPlacement(overlayEle, {
      referenceElement: labelEle,
      ...rest
    });
  }, []);
  
  return (<div className={`datalab-dynamic-date ${className}`} style={style}>
		
    <DateTypeSegment isEn={isEn} value={dateType} onChange={(newDateType: string) => {
      const lastCount = newDateType === 'day' ? 7 : (newDateType === 'hour' ? -2 : 1);
      setValue({
        dateType: newDateType,
        lastCount ,
        dateRange: getLastDateRange(lastCount, newDateType),
        value: [`${lastCount}-${newDateType}`],
        from: '',
        compareTimes: [], // 切换粒度重置
      });
			
    }}/>
		
    <LastCountSelect
      key={dateType}
      value={value}
      onChange={(newValue) => setValue(value => ({...value, ...newValue}))}
      isEn={isEn}
      options={optionsMap[dateType]}
      agoOptions={agoOptionsMap[dateType]}
      formatMessage={formatMessage}
    />
    <TimeCompare
      disabled={isDisabledCompare}
      baseTimeRange={dateRange}
      baseTimeName={fieldName}
      timeGranularity={dateType}
      defaultCompareTimes={compareTimes}
      type={DateShowType.DYNAMIC}
      onCancel={()=> {
        onClose();
      }}
      onConfirm={(compareTimes) => {
        onClose();
        onChange({...value, compareTimes});
      }}
      onStateChange={handleUserAction}
    />
  </div>);
}



function LastCountSelect(props: any) {
  const { value, onChange, isEn, options, agoOptions, formatMessage } = props;
  const { dateType, lastCount } = value || {};

  const isCustom = useMemo(() => {
    const isOpt = options?.some((opt: ListOption) => opt.value === lastCount);
    if (isOpt) {
      if (lastCount === 0) { // 用户无法自定义0
        return value?.from !== '-1';
      }
      return false;
    }
    return true;

  }, [options, lastCount, value?.from]);
  const defaultCount = dateType === 'hour' ? '2' : '3';
  const defaultFrom = dateType === 'hour' ? '-1' : '1';
  /**
   * count 强制是String类型, 这样可以区分用户是输入的自定义天数, 还是选择了定义好的选项天数(Number)
   * 比如原本选项中有 3天, 但是用户也在自定义中输入 3, 那么下次展示的时候要选中自定义选项, 而不是定义好的3天选项.
   */
  const [count, setCount] = useState<string>((isCustom ? value?.lastCount?.toString() : defaultCount) || defaultCount);
  const [from, setFrom] = useState(isCustom ? (value?.from || defaultFrom) : defaultFrom);
  // 代表用户正在手动输入
  const [focusCustom, setFocusCustom] = useState(false);
  
  const isOption = Number(from) < 5;
  const prefix = isEn ? 'Last ' : '最近';
  const suffix = useMemo(() => {
    let pluralSuffix = '';
    if (Number(count) > 1) pluralSuffix = 's';
    switch (dateType) {
      case 'hour': return isEn ? ` hour${pluralSuffix} as of` : '小时，截至';
      case 'day': return isEn ? ` day${pluralSuffix} as of` : '天，截至';
      case 'week': return isEn ? ` week${pluralSuffix} as of` : '周，截至';
      case 'month': return isEn ? ` month${pluralSuffix} as of` : '月，截至';
    }
  }, [isEn, dateType, count]);
  return (<List type='option'>
    {
      options?.map((opt: ListOption) => (<ListItem
        key={opt.text}
        selected={(!isCustom) && value.lastCount == opt.value}
        onClick={() => {
          onChange({
            dateType,
            lastCount: opt.value,
            dateRange: getLastDateRange(opt.value, dateType),
            value: [`${opt.value}-${dateType}`],
            // text: opt.text,
            from: opt.value === 0 ? '-1' : '',
          }, true);
        }}
      >
        {opt.text}
      </ListItem>))
    }
		
    <ListItem
      key={'input'}
      selected={isCustom}
      onClick={() => {
        if (isCustom) return;
        onChange({
          dateType,
          lastCount: count,
          dateRange: getLastDateRange(count, dateType, from),
          value: [`${count}-${dateType}`],
          from,
        }, true);
      }}
    >
      {prefix}
      <Input
        // type={'number'}
        value={count}
        onChange={(value) => {
          if (value && (!/^\d+$/.test(value) || (Number(value) < 1))) return;
          setCount(value);
					
          value && onChange({
            dateType,
            lastCount: value,
            dateRange: getLastDateRange(value, dateType, from),
            value: [`${value}-${dateType}`],
            from,
          }, false);
					
        }}
        onClick={(event) => {
          event.stopPropagation();
          event.nativeEvent.stopImmediatePropagation();
        }}
        onMouseDown={(event) => {
          event.stopPropagation();
          event.nativeEvent.stopImmediatePropagation();
        }}
      />
      {suffix}

      <div
        style={{display: 'inline-block'}}
        onClick={(event) => {
          event.stopPropagation();
          event.nativeEvent.stopImmediatePropagation();
        }}
      >
        <Select
          className={'dbl-dynamic-date-ago'}
          overlayClassName={'dbl-dynamic-date-ago-overlay'}
          appearance="button"
          options={agoOptions}
          value={from}
          listHeight={600}
          listWidth={isEn ? 210 : 180}
          button={isOption ? undefined : formatMessage({id: `dbl_date_${dateType}_ago`}, {ago: from}) }
          footer={(
            <div
              className={classNames('dbl-dynamic-date-ago-footer-content', {
                'is-selected': !isOption
              })}
            >
              <Bubble
                error
                overlayStyle={{zIndex: 999999999}}
                visible={focusCustom && isOption}
                placement="left"
                content={formatMessage({ id: 'dbl_date_last_custom_info' })}
              >
                <NumberInput
                  type={'number'}
                  className={'dbl-rtrs-custom-input'}
                  value={isOption ? undefined : from}
                  onChange={(from) => {
                    setFrom(from);
                    onChange({
                      value: [`${count}-${dateType}`],
                      dateType,
                      lastCount: count,
                      dateRange: getLastDateRange(count, dateType, from),
                      from,
                    }, false);
                  }}
                  allowNegative={false}
                  allowDecimal={false}
                  min={1}
                  onMouseDown={(event) => {
                    event.stopPropagation();
                    event.nativeEvent.stopImmediatePropagation();
                  }}
                  onClick={(event) => {
                    event.stopPropagation();
                    event.nativeEvent.stopImmediatePropagation();
                  }}
                  onFocus={(event) => {
                    setFocusCustom(true);
                    event.stopPropagation();
                    event.nativeEvent.stopImmediatePropagation();
                  }}
                  onBlur={() => {
                    setFocusCustom(false);
                  }}
                  placeholder={formatMessage({id: 'dbl_common_number'})}
                />
              </Bubble>
            
              <span>{formatMessage({id: `dbl_date_text_${dateType}_ago`})}</span>
            </div>
          )}
          onChange={(from) => {
            setFrom(from);
            onChange({
              value: [`${count}-${dateType}`],
              dateType,
              lastCount: count,
              dateRange: getLastDateRange(count, dateType, from),
              from
            }, false);
          }}
        />
      </div>
    </ListItem>
    
  </List>);
}


export default DynamicDate;